import { AsyncPipe, isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { firstValueFrom, of, switchMap } from 'rxjs';
import { getAllRouteData } from '../../../lib/get-all-route-data';
import { getAllRouteParams } from '../../../lib/get-all-route-params';
import { AssetService } from '../../../services/asset.service';
import { GuestAuthService } from '../../../services/guest-auth.service';
import { GuestService } from '../../../services/guest.service';
import { UserService } from '../../../services/user.service';
import { WatcherService } from '../../../services/watcher.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { AssetUrlPipe } from '../../../shared/pipes/asset-url.pipe';

@Component({
  selector: 'app-unsubscribed-from-asset-page',
  templateUrl: './unsubscribed-from-asset-page.component.html',
  styleUrls: ['./unsubscribed-from-asset-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SvgComponent, ButtonDirective, RouterLink, AssetUrlPipe, AsyncPipe],
})
export class UnsubscribedFromAssetComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private watcherService = inject(WatcherService);
  private userService = inject(UserService);
  private guestService = inject(GuestService);
  private guestAuthService = inject(GuestAuthService);
  private assetService = inject(AssetService);
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);
  asset$ = this.route.params.pipe(
    switchMap(() => {
      const data = getAllRouteParams(this.route.snapshot);
      if (isPlatformServer(this.platformId)) return of(undefined);
      return this.assetService.getAssetObservable(data.assetId, data.galleryId, { ignore401Errors: true });
    })
  );
  gallery?: GalleryDto;
  assetId: string;
  galleryId: string;
  additionalAssetCount: number;

  private async getAssetCount() {
    try {
      const [subscriptions, assets] = await Promise.all([
        firstValueFrom(this.watcherService.mySubscriptions$),
        this.assetService.getAssetsForGallery(this.gallery.id, { ignore401Errors: true }),
      ]);
      return assets.filter(img => !!subscriptions.find(s => s.assetId === img.id)).length;
    } catch {}
  }

  async ngOnInit() {
    if (isPlatformServer(this.platformId)) return;
    const params = getAllRouteParams(this.route.snapshot);
    const data = getAllRouteData(this.route.snapshot);
    this.assetId = params.assetId;
    this.galleryId = params.galleryId;
    this.gallery = data.gallery;

    const user = await firstValueFrom(this.userService.getUser());
    let guest = await this.guestService.getGuest();

    const email = this.route.snapshot.queryParams.email;
    if (email && guest && guest.email?.toLowerCase() !== email?.toLowerCase()) guest = undefined;
    if (!user && email && !guest) {
      await this.guestAuthService.authenticateWithEmail('Anonymous', email, undefined, this.gallery);
      guest = await this.guestService.getGuest();
    }

    if (!user && !guest) {
      const resp = await this.guestAuthService.authenticateWithPopup(this.gallery);
      if (!resp) return false;
    }

    this.watcherService.unWatchAsset$.next(this.assetId);
    this.additionalAssetCount = await this.getAssetCount();

    this.check.detectChanges();
  }
}
